import { Link } from 'react-router-dom'
import type { AthleteWithSync, SyncStatus } from '@gd/shared'
import { useAthletes } from '@/api/hooks'
import { formatRelativeTime } from '@/lib/format'
import { cn } from '@/lib/cn'
import { Skeleton } from '@/components/ui/Skeleton'

const STATUS_DOT: Record<SyncStatus, string> = {
  ok: 'bg-status-good',
  running: 'bg-status-warn',
  error: 'bg-status-bad',
}

const STATUS_LABEL: Record<SyncStatus, string> = {
  ok: 'Synced',
  running: 'Syncing',
  error: 'Sync failed',
}

function mostRecent(athletes: AthleteWithSync[]): AthleteWithSync | undefined {
  return [...athletes].sort((a, b) => {
    const at = a.lastSync?.startedAt ?? ''
    const bt = b.lastSync?.startedAt ?? ''
    return bt.localeCompare(at)
  })[0]
}

export interface SyncStatusChipProps {
  /** Show a specific athlete's sync status; defaults to the most recently synced athlete. */
  athleteId?: string
}

function useSyncTarget(athleteId: string | undefined) {
  const { data: athletes, isLoading } = useAthletes()
  const target = athletes
    ? athleteId
      ? athletes.find((a) => a.id === athleteId)
      : mostRecent(athletes)
    : undefined
  return { isLoading, hasAthletes: !!athletes?.length, lastSync: target?.lastSync ?? null }
}

function SyncDot({ status }: { status: SyncStatus | undefined }) {
  return (
    <span
      className={cn('size-1.5 shrink-0 rounded-full', status ? STATUS_DOT[status] : 'bg-ink-3')}
      aria-hidden="true"
    />
  )
}

/** Sync status as plain text (dot, label, time) for a row that already links to the sync page. */
export function SyncStatusText({ athleteId }: SyncStatusChipProps) {
  const { isLoading, hasAthletes, lastSync } = useSyncTarget(athleteId)
  if (isLoading) return <Skeleton className="h-4 w-24 rounded-full" />
  if (!hasAthletes) return null
  return (
    <span className="inline-flex items-center gap-1.5 text-caption text-ink-2">
      <SyncDot status={lastSync?.status} />
      {lastSync ? STATUS_LABEL[lastSync.status] : 'No syncs yet'}
      {lastSync?.finishedAt && (
        <span className="text-ink-3">{formatRelativeTime(lastSync.finishedAt)}</span>
      )}
    </span>
  )
}

/** Chip showing sync status colour + relative time, linking through to the sync page. */
export function SyncStatusChip({ athleteId }: SyncStatusChipProps) {
  const { isLoading, hasAthletes, lastSync } = useSyncTarget(athleteId)

  if (isLoading) return <Skeleton className="h-7 w-28 rounded-full" />
  if (!hasAthletes) return null

  return (
    <Link
      to="/sync"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-2 px-2.5 py-1 text-caption text-ink-2',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
      )}
    >
      <SyncDot status={lastSync?.status} />
      {lastSync ? STATUS_LABEL[lastSync.status] : 'No syncs yet'}
      {lastSync?.finishedAt && (
        <span className="text-ink-3">{formatRelativeTime(lastSync.finishedAt)}</span>
      )}
    </Link>
  )
}
